import React, { FC, memo } from 'react';
import { Box, Flex, Image, Text, useBreakpointValue } from '@chakra-ui/react';
import type { IPhotoActions, IPhotoWithUrl, IRootScope } from 'ts/client/types';
import { MobileBlurImage } from 'ts/common/components/gallery/screenshot-deterrence/MobileBlur';
import useMobileBlur from 'ts/common/components/gallery/screenshot-deterrence/MobileBlurContext';
import PhotoToolbar from 'client_react/gallery/components/PhotoToolbar';

interface Props {
    /** True only if the photo has black & white enabled */
    isBlackAndWhite: boolean;
    /** True only if the download button should be visible */
    isDownloadPossible: boolean;
    /** True only if the photo is favorited */
    isFavorite: boolean;
    /** True only if the photo is hidden */
    isHidden: boolean;
    /** True only if the photo has at least one label applied */
    isLabeled: boolean;
    /** The photo to render */
    photo: IPhotoWithUrl;
    /** An object with callbacks to perform actions on photos */
    photoActions: IPhotoActions;
    /** True if the user needs to select a download destination for a pending download */
    requiresDownloadDestination: boolean;
    /** Angular free digitals service */
    spFreeDigitals: SpAngularJs.ISpFreeDigitals;
    /** Angular translation service */
    translateFilter: SpAngularJs.ITranslateFilter;
    /** Angular root scope */
    $rootScope: IRootScope;
}

const PhotoComparePanel: FC<Props> = ({
    isBlackAndWhite,
    isDownloadPossible,
    isFavorite,
    isHidden,
    isLabeled,
    photo,
    ...rest
}) => {
    const { blur, pressHandlers, onContextMenu } = useMobileBlur('lightbox');
    const photoFilter = isBlackAndWhite ? 'grayscale(100%)' : undefined;

    return (
        <Flex alignItems="center" flexDirection="column" gap="24px" width="100%">
            <Flex
                alignItems="stretch"
                alignSelf="stretch"
                flexDirection="column"
                flexGrow={1}
                gap={useBreakpointValue({ base: '16px', lg: '20px' })}
            >
                <Text
                    alignSelf="center"
                    fontSize="xs"
                    maxWidth={useBreakpointValue({ base: '100vw', lg: '40vw' })}
                    overflow="hidden"
                    paddingX="8px"
                    textOverflow="ellipsis"
                    whiteSpace="nowrap"
                >
                    {photo.filename}
                </Text>
                <Box
                    data-testid="comparePhotoImage"
                    flexGrow={1}
                    position="relative"
                    {...pressHandlers}
                >
                    <Image
                        alt={photo.filename}
                        draggable={false}
                        filter={photoFilter}
                        left="50%"
                        maxHeight="100%"
                        maxWidth="100%"
                        onContextMenu={onContextMenu}
                        position="absolute"
                        src={photo.url}
                        top="50%"
                        transform="translate(-50%, -50%)"
                    />
                    {blur && (
                        <MobileBlurImage
                            {...blur}
                            filter={photoFilter}
                            fit="contain"
                            src={photo.url}
                        />
                    )}
                </Box>
            </Flex>
            <PhotoToolbar
                isBlackAndWhite={isBlackAndWhite}
                isDownloadPossible={isDownloadPossible}
                isFavorite={isFavorite}
                isHidden={isHidden}
                isLabeled={isLabeled}
                photo={photo}
                {...rest}
            />
        </Flex>
    );
};

export default memo(PhotoComparePanel);
